import React, { useEffect, useState } from "react";
import { TransProps } from "@lingui/react";
import { Trans } from "@lingui/macro";
import { getColor } from "../../../../../utils";
import { dateFilterValues, toStringDate } from "../../index";
import { useDispatch, useSelector } from "react-redux";
import { typeStatisticsState } from "../../../../../../model/dashboardStatistics/dStatisticsReducer";
import {
  dStatisticsSetDataSagaAC,
  dStatisticsSetPeriodAC,
} from "../../../../../../model/dashboardStatistics/dStatisticsActionCreators";
import { PopupPanelDashboard } from "../../../DashboardContentTable/PopupPanelDashboard";
import { PopupStories } from "../../popupContents/PopupStories";
import { PopupPeriod } from "../../popupContents/PopupPeriod";

// Types
export type typeDateFilterItem = {
  id: string;
  checked: boolean;
  name: string | React.ReactElement<TransProps>;
};

export type typeDateFilter = {
  variant?: string;
};

// Component
export const DateFilter: React.FC<typeDateFilter> = ({ variant }) => {
  const dispatch = useDispatch();

  const color = getColor(variant);
  let bgColor = "";
  let borderColor = "";
  let focusItem = "";
  let focusWrapper = "";
  switch (color) {
    case "primary":
      bgColor = "bg-primary-600";
      borderColor = "border-primary-600";
      focusItem = "focus:bg-primary-200 focus:border-primary-200";
      focusWrapper =
        "focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500";
      break;
    case "indigo":
      bgColor = "bg-indigo-600";
      borderColor = "border-indigo-600";
      focusItem = "focus:bg-indigo-200 focus:border-indigo-200";
      focusWrapper =
        "focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500";
      break;
  }

  const period = useSelector(
    (state: { dStatistics: typeStatisticsState }) => state.dStatistics.period,
  );
  const dateFilterId = useSelector(
    (state: { dStatistics: typeStatisticsState }) =>
      state.dStatistics.dateFilterId,
  );

  const onSelect = (button: string) => {
    setDateFilterList(
      dateFilterList.map((item) => {
        return {
          id: item.id,
          checked: button === item.id,
          name: item.name,
        };
      }),
    );

    switch (button) {
      case dateFilterValues.YESTERDAY:
        let yesterdayFrom = new Date();
        yesterdayFrom.setDate(yesterdayFrom.getDate() - 1);
        yesterdayFrom.setHours(0, 0, 0, 0);
        let yesterdayTo = new Date();
        yesterdayTo.setDate(yesterdayTo.getDate() - 1);
        yesterdayTo.setHours(23, 59, 59, 999);

        dispatch(
          dStatisticsSetPeriodAC(
            {
              dateFrom: yesterdayFrom.toISOString(),
              dateTo: yesterdayTo.toISOString(),
            },
            button,
          ),
        );
        break;

      case dateFilterValues.WEEK:
        let weekFrom = new Date();
        weekFrom.setDate(weekFrom.getDate() - 7);
        weekFrom.setHours(0, 0, 0, 0);
        let weekTo = new Date();
        weekTo.setHours(23, 59, 59, 999);

        dispatch(
          dStatisticsSetPeriodAC(
            {
              dateFrom: weekFrom.toISOString(),
              dateTo: weekTo.toISOString(),
            },
            button,
          ),
        );
        break;

      case dateFilterValues.MONTH:
        let monthFrom = new Date();

        monthFrom.setMonth(monthFrom.getMonth() - 1);
        monthFrom.setHours(0, 0, 0, 0);
        let monthTo = new Date();
        monthTo.setHours(23, 59, 59, 999);

        dispatch(
          dStatisticsSetPeriodAC(
            {
              dateFrom: monthFrom.toISOString(),
              dateTo: monthTo.toISOString(),
            },
            button,
          ),
        );
        break;

      case dateFilterValues.PERIOD:
        setOpenPeriodPopup(true);
        break;

      default:
        let todayFrom = new Date();
        todayFrom.setHours(0, 0, 0, 0);
        let todayTo = new Date();
        todayTo.setHours(23, 59, 59, 999);

        dispatch(
          dStatisticsSetPeriodAC(
            {
              dateFrom: todayFrom.toISOString(),
              dateTo: todayTo.toISOString(),
            },
            dateFilterValues.TODAY,
          ),
        );
    }
  };

  const filterList: typeDateFilterItem[] = [
    { id: dateFilterValues.TODAY, checked: false, name: <Trans>Today</Trans> },
    {
      id: dateFilterValues.YESTERDAY,
      checked: false,
      name: <Trans>Yesterday</Trans>,
    },
    { id: dateFilterValues.WEEK, checked: true, name: <Trans>Week</Trans> },
    { id: dateFilterValues.MONTH, checked: false, name: <Trans>Month</Trans> },
    {
      id: dateFilterValues.PERIOD,
      checked: false,
      name: <Trans>Period</Trans>,
    },
  ];

  const [dateFilterList, setDateFilterList] = useState(
    filterList.map((item) => {
      item.checked = item.id === dateFilterId;
      return item;
    }),
  );

  // Popups
  const [isOpenPeriodPopup, setOpenPeriodPopup] = useState(false);
  const [isOpenStorePopup, setOpenStorePopup] = useState(false);

  useEffect(() => {
    const dateFrom = new Date(period.dateFrom);
    const dateTo = new Date(period.dateTo);

    dispatch(
      dStatisticsSetDataSagaAC({
        storeIds: [],
        dateFrom: toStringDate(dateFrom),
        dateTo: toStringDate(dateTo),
      }),
    );
    // eslint-disable-next-line
  }, [period]);

  return (
    <>
      <div className="sticky top-0 bg-white pt-2 pb-2 z-30 flex justify-between gap-4 focus:hover:shadow-md">
        <div className={`rounded flex ${focusWrapper}`}>
          {dateFilterList.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className={`px-4 py-2 cursor-pointer border-0 focus:outline-none ${item.checked ? ` ${bgColor} text-white` : focusItem}${item.checked ? " " + borderColor : ""} border-t border-b border-r${index === 0 ? " border-l rounded-l" : ""}${index === dateFilterList.length - 1 ? " rounded-r" : ""}`}
              onClick={() => onSelect(item.id)}
            >
              {item.name}
            </button>
          ))}
        </div>
        {/*
                <Button type="button" secondary={true} icon={<ShoppingCartIcon className="h-5 w-5 mr-2"/>} label={<Trans>Stories</Trans>} onClick={() => setOpenStorePopup(true)}/>
            */}
      </div>

      <PopupPanelDashboard
        title={<Trans>Stories</Trans>}
        openPopup={isOpenStorePopup}
        setOpenPopup={setOpenStorePopup}
      >
        <PopupStories setOpenStoriesPopup={setOpenStorePopup} />
      </PopupPanelDashboard>

      <PopupPanelDashboard
        title={<Trans>Set period</Trans>}
        openPopup={isOpenPeriodPopup}
        setOpenPopup={setOpenPeriodPopup}
      >
        <PopupPeriod setOpenPeriodPopup={setOpenPeriodPopup} />
      </PopupPanelDashboard>
    </>
  );
};
